import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { addRobotoFont } from './robotoFont';

// Helper function to format date consistently
const formatDate = (dateString, withTime = false) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return format(date, withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy', { locale: cs });
  } catch {
    return dateString;
  }
};

// Helper function to get change type label in Czech
const getChangeTypeLabel = (changeType) => {
  switch (changeType) {
    case 'create': return 'Vytvoření';
    case 'status': return 'Změna stavu';
    case 'edit': return 'Úprava';
    case 'image': return 'Nahrání obrázku';
    case 'file': return 'Nahrání souboru';
    case 'comment': return 'Komentář';
    default: return 'Změna';
  }
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const exportReportToPDF = (report, comments, history) => {
  try {
    const doc = new jsPDF();
    
    // Add Roboto font for Czech diacritics support
    addRobotoFont(doc);
    doc.setFont('Roboto');
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
  
  // Colors
  const primaryColor = [15, 118, 110]; // Teal
  const headerBgColor = [15, 118, 110];
  const headerTextColor = [255, 255, 255];
  const altRowColor = [240, 253, 250]; // Light teal
  const textColor = [30, 41, 59]; // Slate 800
  
  // ==================== HEADER ====================
  // Header background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  // Title
  doc.setTextColor(...headerTextColor);
  doc.setFontSize(22);
  doc.setFont('Roboto', 'normal');
  doc.text('PROTOKOL HLÁŠENÍ KVALITY', pageWidth / 2, 18, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(12);
  doc.setFont('Roboto', 'normal');
  doc.text('Kvalita Zdravé Stravování', pageWidth / 2, 28, { align: 'center' });
  
  // Export date
  doc.setFontSize(9);
  doc.text(`Vygenerováno: ${formatDate(new Date(), true)}`, pageWidth / 2, 36, { align: 'center' });
  
  let y = 50;
  
  // ==================== REPORT TITLE ====================
  doc.setTextColor(...textColor);
  doc.setFontSize(16);
  doc.setFont('Roboto', 'normal');
  doc.text(report.title, 14, y);
  y += 8;
  
  // Status badge
  doc.setFontSize(10);
  doc.setFont('Roboto', 'normal');
  const statusText = `Stav: ${report.status_name}`;
  doc.text(statusText, 14, y);
  y += 12;
  
  // ==================== ZÁKLADNÍ INFORMACE ====================
  doc.setFontSize(12);
  doc.setFont('Roboto', 'normal');
  doc.setTextColor(...primaryColor);
  doc.text('ZÁKLADNÍ INFORMACE', 14, y);
  y += 2;
  
  // Main info table
  const mainInfoData = [
    ['Šarže', report.batch || '-'],
    ['Počet kusů', report.quantity?.toString() || '-'],
    ['Kategorie závady', report.category_name || 'Bez kategorie'],
    ['Aktuální stav', report.status_name || '-'],
    ['Autor hlášení', report.created_by_name || '-'],
    ['Datum vytvoření', formatDate(report.created_at, true)],
    ['Poslední aktualizace', formatDate(report.updated_at, true)]
  ];
  
  autoTable(doc, {
    startY: y,
    head: [],
    body: mainInfoData,
    theme: 'plain',
    styles: {
      font: 'Roboto',
      fontSize: 10,
      cellPadding: 4,
      textColor: textColor,
      lineColor: [226, 232, 240],
      lineWidth: 0.1
    },
    columnStyles: {
      0: { 
        fontStyle: 'bold', 
        cellWidth: 50,
        fillColor: altRowColor
      },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 },
    tableLineColor: [226, 232, 240],
    tableLineWidth: 0.5
  });
  
  y = doc.lastAutoTable.finalY + 15;
  
  // ==================== VLASTNÍ POLE ====================
  if (report.custom_fields && Object.keys(report.custom_fields).length > 0) {
    // Check if we need a new page
    if (y > pageHeight - 60) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...primaryColor);
    doc.text('DALŠÍ INFORMACE', 14, y);
    y += 2;
    
    const customFieldsData = Object.entries(report.custom_fields).map(([key, value]) => {
      // Format date values
      let displayValue = value;
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
        displayValue = formatDate(value);
      }
      return [key, displayValue || '-'];
    });
    
    autoTable(doc, {
      startY: y,
      head: [],
      body: customFieldsData,
      theme: 'plain',
      styles: {
        font: 'Roboto',
        fontSize: 10,
        cellPadding: 4,
        textColor: textColor,
        lineColor: [226, 232, 240],
        lineWidth: 0.1
      },
      columnStyles: {
        0: { 
          fontStyle: 'bold', 
          cellWidth: 50,
          fillColor: altRowColor
        },
        1: { cellWidth: 'auto' }
      },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 15;
  }
  
  // ==================== NOTIFIKACE ====================
  if ((report.notification_emails && report.notification_emails.length > 0) || 
      (report.dmt_notification && report.dmt_notification.enabled)) {
    
    if (y > pageHeight - 60) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...primaryColor);
    doc.text('NASTAVENÍ NOTIFIKACÍ', 14, y);
    y += 2;
    
    const notificationData = [];
    
    if (report.notification_emails && report.notification_emails.length > 0) {
      notificationData.push(['Notifikace při změnách', report.notification_emails.join(', ')]);
    }
    
    if (report.dmt_notification && report.dmt_notification.enabled) {
      notificationData.push([
        'Notifikace DMT', 
        `${report.dmt_notification.days_before} dní před vypršením\nPříjemci: ${report.dmt_notification.emails?.join(', ') || '-'}`
      ]);
    }
    
    if (notificationData.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [],
        body: notificationData,
        theme: 'plain',
        styles: {
          font: 'Roboto',
          fontSize: 10,
          cellPadding: 4,
          textColor: textColor,
          lineColor: [226, 232, 240],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { 
            fontStyle: 'bold', 
            cellWidth: 50,
            fillColor: altRowColor
          },
          1: { cellWidth: 'auto' }
        },
        margin: { left: 14, right: 14 }
      });
      
      y = doc.lastAutoTable.finalY + 15;
    }
  }
  
  // ==================== KOMENTÁŘE ====================
  if (comments && comments.length > 0) {
    if (y > pageHeight - 80) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...primaryColor);
    doc.text(`KOMENTÁŘE (${comments.length})`, 14, y);
    y += 2;
    
    const commentsData = comments.map((comment, index) => [
      (index + 1).toString(),
      comment.user_name || '-',
      formatDate(comment.created_at, true),
      comment.content || '-'
    ]);
    
    autoTable(doc, {
      startY: y,
      head: [['#', 'Autor', 'Datum a čas', 'Obsah komentáře']],
      body: commentsData,
      theme: 'striped',
      headStyles: {
        font: 'Roboto',
        fillColor: headerBgColor,
        textColor: headerTextColor,
        fontStyle: 'bold',
        fontSize: 10
      },
      styles: {
        font: 'Roboto',
        fontSize: 9,
        cellPadding: 4,
        textColor: textColor,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 'auto' }
      },
      alternateRowStyles: {
        fillColor: altRowColor
      },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 15;
  }
  
  // ==================== HISTORIE ZMĚN ====================
  if (history && history.length > 0) {
    if (y > pageHeight - 80) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...primaryColor);
    doc.text(`HISTORIE ZMĚN (${history.length})`, 14, y);
    y += 2;
    
    const historyData = history.map((item, index) => {
      let description = '';
      
      if (item.change_type === 'create') {
        description = `Vytvořeno se stavem: ${item.new_status}`;
      } else if (item.change_type === 'status') {
        description = `${item.old_status || '-'} → ${item.new_status}`;
      } else if (item.description) {
        description = item.description;
      } else if (item.old_status && item.new_status) {
        description = `${item.old_status} → ${item.new_status}`;
      } else if (item.new_status) {
        description = `Vytvořeno: ${item.new_status}`;
      } else {
        description = 'Změna';
      }
      
      return [
        (index + 1).toString(),
        getChangeTypeLabel(item.change_type),
        description,
        item.changed_by_name || '-',
        formatDate(item.changed_at, true)
      ];
    });
    
    autoTable(doc, {
      startY: y,
      head: [['#', 'Typ změny', 'Popis', 'Provedl', 'Datum a čas']],
      body: historyData,
      theme: 'striped',
      headStyles: {
        font: 'Roboto',
        fillColor: headerBgColor,
        textColor: headerTextColor,
        fontStyle: 'bold',
        fontSize: 10
      },
      styles: {
        font: 'Roboto',
        fontSize: 9,
        cellPadding: 4,
        textColor: textColor,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 25 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 30 },
        4: { cellWidth: 35 }
      },
      alternateRowStyles: {
        fillColor: altRowColor
      },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 15;
  }
  
  // ==================== PŘÍLOHY ====================
  const attachments = report.attachments || [];
  const images = attachments.filter(a => a.is_image);
  const files = attachments.filter(a => !a.is_image);
  
  if (attachments.length > 0) {
    if (y > pageHeight - 60) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(...primaryColor);
    doc.text(`PŘÍLOHY (${attachments.length})`, 14, y);
    y += 2;
    
    const attachmentData = [];
    
    if (images.length > 0) {
      attachmentData.push(['Obrázky', `${images.length} obrázků`]);
    }
    
    files.forEach(file => {
      attachmentData.push([file.name, `${formatFileSize(file.size)} • ${file.type?.split('/')[1]?.toUpperCase() || 'Soubor'}`]);
    });
    
    autoTable(doc, {
      startY: y,
      head: [['Název souboru', 'Velikost / Typ']],
      body: attachmentData,
      theme: 'striped',
      headStyles: {
        font: 'Roboto',
        fillColor: headerBgColor,
        textColor: headerTextColor,
        fontStyle: 'bold',
        fontSize: 10
      },
      styles: {
        font: 'Roboto',
        fontSize: 9,
        cellPadding: 4,
        textColor: textColor
      },
      alternateRowStyles: {
        fillColor: altRowColor
      },
      margin: { left: 14, right: 14 }
    });
    
    y = doc.lastAutoTable.finalY + 8;
    
    doc.setFontSize(9);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('Pro zobrazení příloh otevřete hlášení v aplikaci.', 14, y);
  }
  
  // ==================== FOOTER ====================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(226, 232, 240);
    doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);
    
    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184); // Slate 400
    doc.setFont('Roboto', 'normal');
    doc.text(
      `Strana ${i} z ${pageCount}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );
    
    doc.text(
      'Kvalita Zdravé Stravování - Systém evidence závad',
      14,
      pageHeight - 8
    );
    
    doc.text(
      formatDate(new Date()),
      pageWidth - 14,
      pageHeight - 8,
      { align: 'right' }
    );
  }
  
  // ==================== SAVE ====================
  const fileName = `Hlaseni_${report.batch || 'export'}_${format(new Date(), 'dd-MM-yyyy')}.pdf`;
  doc.save(fileName);
  
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

export const exportReportsToExcel = (reports) => {
  // Prepare data
  const data = reports.map(report => ({
    'Název': report.title,
    'Šarže': report.batch,
    'Počet kusů': report.quantity,
    'Kategorie': report.category_name || 'Bez kategorie',
    'Stav': report.status_name,
    'Vytvořil': report.created_by_name,
    'Vytvořeno': formatDate(report.created_at, true),
    'Aktualizováno': formatDate(report.updated_at, true)
  }));
  
  // Create workbook
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Hlášení');
  
  // Set column widths
  const colWidths = [
    { wch: 30 },  // Název
    { wch: 15 },  // Šarže
    { wch: 12 },  // Počet kusů
    { wch: 20 },  // Kategorie
    { wch: 15 },  // Stav
    { wch: 20 },  // Vytvořil
    { wch: 20 },  // Vytvořeno
    { wch: 20 }   // Aktualizováno
  ];
  ws['!cols'] = colWidths;
  
  // Save
  XLSX.writeFile(wb, `Hlaseni_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
};
